<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  provide,
  watch,
  useSlots,
  nextTick,
} from 'vue';
import MapLibreGL from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const props = defineProps({
  center: {
    type: Array,
    default: () => [2.3522, 48.8566], // Paris
  },
  zoom: {
    type: Number,
    default: 12,
  },
  theme: {
    type: String,
    default: 'light', // 'light' | 'dark'
  },
  styles: {
    type: Object,
    default: () => ({
      light: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      dark: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
    }),
  },
  projection: {
    type: Object,
    default: null,
  },
  options: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['load', 'click']);

const containerRef = ref(null);
const mapInstance = ref(null);
const isLoaded = ref(false);
const isStyleLoaded = ref(false);

const getResolvedStyle = () => {
  return props.theme === 'dark' ? props.styles.dark : props.styles.light;
};

provide('mapContext', {
  map: mapInstance,
  isLoaded,
  isStyleLoaded,
});

onMounted(() => {
  if (!containerRef.value) return;

  const map = new MapLibreGL.Map({
    container: containerRef.value,
    style: getResolvedStyle(),
    center: props.center,
    zoom: props.zoom,
    renderWorldCopies: false,
    attributionControl: {
      compact: true,
    },
    ...props.options,
  });

  map.on('load', () => {
    isLoaded.value = true;
    if (props.projection) {
      map.setProjection(props.projection);
    }
    emit('load', map);
  });

  map.on('styledata', () => {
    // Style data can be emitted multiple times
    isStyleLoaded.value = true;
  });

  map.on('click', e => {
    emit('click', e);
  });

  // Handle resizing
  const resizeObserver = new ResizeObserver(() => {
    map.resize();
  });
  resizeObserver.observe(containerRef.value);

  mapInstance.value = map;
});

onUnmounted(() => {
  if (mapInstance.value) {
    mapInstance.value.remove();
    mapInstance.value = null;
  }
});

watch(
  () => props.theme,
  newTheme => {
    if (!mapInstance.value) return;
    const newStyle = getResolvedStyle();
    isStyleLoaded.value = false;
    mapInstance.value.setStyle(newStyle, { diff: true });
  }
);

watch(
  () => props.center,
  newCenter => {
    if (!mapInstance.value) return;
    mapInstance.value.setCenter(newCenter);
  },
  { deep: true }
);

watch(
  () => props.zoom,
  newZoom => {
    if (!mapInstance.value) return;
    mapInstance.value.setZoom(newZoom);
  }
);

defineExpose({
  map: mapInstance,
});
</script>

<template>
  <div ref="containerRef" class="relative w-full h-full min-h-[inherit]">
    <div
      v-if="!isLoaded"
      class="absolute inset-0 flex items-center justify-center bg-n-alpha-1 z-10"
    >
      <div class="flex gap-1.5">
        <div class="size-2 rounded-full bg-n-brand/40 animate-pulse" />
        <div
          class="size-2 rounded-full bg-n-brand/40 animate-pulse [animation-delay:200ms]"
        />
        <div
          class="size-2 rounded-full bg-n-brand/40 animate-pulse [animation-delay:400ms]"
        />
      </div>
    </div>
    <slot v-if="isLoaded" />
  </div>
</template>

<style>
/* Reset popups common styles if needed */
.maplibregl-popup-content {
  @apply bg-transparent shadow-none p-0 rounded-none;
}
.maplibregl-popup-tip {
  @apply hidden;
}
</style>
